import { useState } from 'react';
import { Search, ChevronUp, ChevronDown, Inbox } from 'lucide-react';

function DataTable({ columns, data, actions, searchable = true, pageSize = 10 }) {
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const filtered = data.filter(row => {
    if (!search) return true;
    const term = search.toLowerCase();
    // Check columns with explicit accessor or searchValue first
    const colMatch = columns.some(col => {
      let val = '';
      if (col.searchValue) val = col.searchValue(row);
      else if (col.accessor) val = row[col.accessor];
      return String(val || '').toLowerCase().includes(term);
    });
    if (colMatch) return true;
    // Fallback: search all string fields in the row data
    return Object.values(row).some(v =>
      typeof v === 'string' && v.toLowerCase().includes(term)
    );
  });

  const sorted = sortCol ? [...filtered].sort((a, b) => {
    const av = a[sortCol] || '';
    const bv = b[sortCol] || '';
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  }) : filtered;

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      {searchable && (
        <div className="p-3 border-b border-gray-100">
          <div className="relative max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search..."
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {columns.map(col => (
                <th key={col.key || col.accessor}
                  className={`px-4 py-2 text-left text-sm font-semibold ${col.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                  onClick={() => col.sortable && handleSort(col.accessor)}>
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortCol === col.accessor && (
                      sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                    )}
                  </div>
                </th>
              ))}
              {actions && <th className="px-4 py-2 text-left text-sm font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <Inbox size={36} strokeWidth={1.2} />
                    <p className="text-sm font-medium">{search ? 'No results match your search' : 'No data available'}</p>
                  </div>
                </td>
              </tr>
            ) : paginated.map((row, i) => (
              <tr key={row.id || i} className="hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                {columns.map(col => (
                  <td key={col.key || col.accessor} className="px-4 py-1" style={{ color: '#333' }}>
                    {col.render ? col.render(row, (page - 1) * pageSize + i) : row[col.accessor] || '-'}
                  </td>
                ))}
                {actions && <td className="px-4 py-1"><div className="flex items-center gap-2">{actions(row)}</div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-4 py-1 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="text-gray-500">Showing {(page-1)*pageSize+1}–{Math.min(page*pageSize, sorted.length)} of {sorted.length}</span>
          <div className="flex gap-1">
            <button disabled={page===1} onClick={()=>setPage(p=>p-1)} className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            {Array.from({length:Math.min(totalPages,5)},(_,i)=>i+1).map(p=>(
              <button key={p} onClick={()=>setPage(p)} className={`px-3 py-1 rounded border ${page===p?'bg-purple-600 text-white border-purple-600':'border-gray-200 hover:bg-gray-50'}`}>{p}</button>
            ))}
            <button disabled={page===totalPages} onClick={()=>setPage(p=>p+1)} className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
export default DataTable;
